import React, { type ReactElement, type ReactNode } from 'react';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import ClearFiltersButton from 'atom-components/buttons/clear-filters-button';
import styles from './index.module.scss';

type FilterItemProps = {
  children: ReactNode;
  onClick: () => void;
  hasBadge?: boolean;
};

function FilterItem({ onClick, children, hasBadge }: FilterItemProps): ReactElement {
  return (
    <span className={styles.filterItem}>
      {hasBadge ? (
        children
      ) : (
        <span className={clsx(styles.filterItemBadge, 'badge bg-secondary')}>{children}</span>
      )}
      <button type="button" className="btn btn-danger" onClick={onClick}>
        <FontAwesomeIcon icon={faCircleXmark} size="sm" className="mx-1" fixedWidth />
      </button>
    </span>
  );
}

type FilterProps = {
  name: string;
  children: ReactNode;
};

function Filter({ name, children }: FilterProps): ReactElement {
  return (
    <li className={clsx(styles.filterGroupWrapper, 'text-muted me-2')}>
      <div className={clsx(styles.filterGroup)}>
        {name}:<div className={clsx(styles.filterItemWrapper)}>{children}</div>
      </div>
    </li>
  );
}

type AppliedFiltersProps = {
  canClear: boolean;
  onClear: () => void;
  children: ReactNode;
};

function AppliedFilters({ canClear, onClear, children }: AppliedFiltersProps): ReactElement {
  return (
    <div>
      <ul className={styles.appliedFilters}>{children}</ul>
      <ClearFiltersButton className={styles.clearButton} disabled={!canClear} onClick={onClear} />
    </div>
  );
}

/**
 * A lightweight set of components for composing a list of applied filters for tables, reports, and
 * other multi-state UI elements. Contains a pre-defined outer layout/shape, with the option to
 * pass in arbitrary badges to represent filter items. */
export default Object.assign(AppliedFilters, {
  Filter,
  FilterItem
});
